/**
 * Phase 41 Plan 41-05 Task 1 — AsProjectManagerCard.
 *
 * PM-only dashboard card; rendered conditionally by DashboardPage when the
 * authenticated user manages at least one project AND is not impersonating
 * (Pitfall 5). Per checker I-23, DashboardPage renders a skeleton while
 * `useMyProjects` is loading so the slot does NOT pop in/out on first paint.
 *
 * Data path note: `useMyProjects()` returns `ProjectListItem[]` from
 * `GET /api/projects?memberOf=true`. Each row has `projectManager: { id, name } | null`
 * — NOT a flat `projectManagerId` (the planner's verbatim example assumed a
 * field that does not exist on the list response). Filtering uses
 * `p.projectManager?.id === user.id`.
 */
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { useMyProjects } from '../../hooks/useMyProjects';
import { useAuth } from '../../services/authContext';

export function AsProjectManagerCard() {
  const { user } = useAuth();
  const { data, isLoading } = useMyProjects();

  if (isLoading || !data || !user) {
    return (
      <Card title="My projects">
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      </Card>
    );
  }

  const managed = data
    .filter((p) => p.projectManager?.id === user.id && p.state === 'in_progress')
    .slice(0, 5);

  if (managed.length === 0) {
    return (
      <Card title="My projects">
        <p className="text-[12px] text-ms-text-muted">No active projects assigned to you.</p>
      </Card>
    );
  }

  return (
    <Card title="My projects">
      <ul className="divide-y divide-ms-border">
        {managed.map((p) => (
          <li key={p.id}>
            <Link
              to={`/projects/${p.id}`}
              className="block py-2 -mx-2 px-2 rounded hover:bg-ms-surface-2"
            >
              <div className="text-[12.5px] font-semibold text-ms-text-strong truncate">
                {p.name}
              </div>
              <div className="text-[10.5px] text-ms-text-muted">
                {p.customer?.name ?? '—'}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
